import { useEffect, useMemo, useState } from 'react';
import { MdConfirmationNumber } from 'react-icons/md';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import useManageTickets from 'legacy/events/hooks/useManageTickets';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';

import Tickets from './Tickets';
import FeesPaidBy from './FeesPaidBy';

import {
  MAX_COMPLIMENTARY_TICKETS,
  MAX_PRIVATE_TICKETS,
  MAX_PUBLIC_TICKETS
} from 'legacy/events/service';
import { useGetMe } from 'legacy/user/hooks';
import ComplimentaryTicketForm from './forms/ComplimentaryTicketForm';
import {
  useCreateEventTier,
  useDeleteEventTier,
  useGetComplimentaryTickets,
  useGetEventTiers,
  useReorderTiersMutation,
  useToggleHideTier,
  useUpdateEventTier
} from '../../../hooks';
import ComplimentaryTickets from './ComplimentaryTickets';
import { CreateTierDto, SectionDto, TierDto } from 'legacy/events/types';
import { showError } from 'legacy/common/utils/toast';
import TicketForm from './forms/TicketForm';
import Sections from './Sections';

const PUBLIC_TAB_INDEX = 0;
const PRIVATE_TAB_INDEX = 1;
const COMPLIMENTARY_TAB_INDEX = 2;

const PricingTiersModal = ({
  onClose,
  eventId,
  eventShareToken,
  refetchEvent,
  createDraft
}) => {
  const [tabIndex, setTabIndex] = useState(PUBLIC_TAB_INDEX);
  const [lastCreatedTicketId, setLastCreatedTicketId] = useState<string | null>(
    null
  );
  const { data: complimentaryTickets, refetch } =
    useGetComplimentaryTickets(eventId);
  const { data } = useGetEventTiers(eventId);
  const tiers = data?.items || [];

  const { data: user } = useGetMe();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const {
    tickets,
    selectedTicket,
    selectedIndex,
    addTicket,
    deleteTicket,
    enableTicket,
    disableTicket,
    setSelectedIndex,
    setSelectedTicket,
    updateTicket,
    setTickets,
    sections,
    setSections
  } = useManageTickets(tiers || [], data?.sections || []);

  const { mutateAsync: createEventTier } = useCreateEventTier(eventId);
  const { mutateAsync: updateEventTier } = useUpdateEventTier(eventId);
  const { mutateAsync: deleteEventTier } = useDeleteEventTier(eventId);

  const reorderEventTierMutation = useReorderTiersMutation();

  const { mutateAsync: toggleHideTier } = useToggleHideTier();

  useEffect(() => {
    // Create a draft if event has not been created yet
    if (!eventId) {
      createDraft();
    }
  }, [eventId]);

  const toggleModal = () => {
    setIsModalVisible(!isModalVisible);
  };

  // Sort tickets by public first, then private
  const sortedTickets = useMemo(
    () =>
      tickets &&
      tickets?.sort((a, b) => {
        if (a.isPrivate === b.isPrivate) {
          return 0;
        }

        if (a.isPrivate) {
          return 1;
        }

        return -1;
      }),
    [tickets]
  );

  const publicTickets = sortedTickets.filter((ticket) => !ticket.isPrivate);
  const privateTickets = sortedTickets.filter((ticket) => ticket.isPrivate);

  const getCurrentTierType = () => {
    if (tabIndex === PUBLIC_TAB_INDEX) {
      return 'public';
    }

    if (tabIndex === PRIVATE_TAB_INDEX) {
      return 'private';
    }

    if (tabIndex === COMPLIMENTARY_TAB_INDEX) {
      return 'complimentary';
    }

    return null;
  };

  const currentTierType = getCurrentTierType();

  const updateEventPricingTier = (values: TierDto) => {
    const {
      type,
      name,
      description,
      price,
      isPrivate,
      isDisabled,
      requiresApproval,
      maxAttendance,
      requiredAssociationIds,
      requiredOrganizationIds,
      questions,
      imageUrl,
      startDate,
      endDate
    } = values;

    updateEventTier(
      {
        tierId: selectedTicket.id,
        tierData: {
          type,
          name,
          description,
          price,
          isPrivate,
          isDisabled,
          requiresApproval,
          maxAttendance,
          requiredAssociationIds,
          requiredOrganizationIds,
          imageUrl,
          startDate,
          endDate,
          questions: questions || []
        }
      },
      {
        onSuccess: (tier) => {
          updateTicket(selectedIndex, {
            ...tier,
            ...(values.sortIndex && { sortIndex: values.sortIndex })
          });
          refetchEvent();
        },
        onError: (error) => {
          showError({
            title: 'Error updating tier',
            message: error?.message
          });
        }
      }
    );
    setSelectedTicket(null);
  };

  const addEventPricingTier = (values: TierDto) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, canDelete, totalGuests, ...ticketDetails } = values;
    const tierData: CreateTierDto = {
      ...ticketDetails,
      questions: values.questions || []
    };

    createEventTier(tierData, {
      onSuccess: (tier) => {
        addTicket(tier);
        setLastCreatedTicketId(tier.id);
        refetchEvent();
      },
      onError: (error) => {
        showError({
          title: 'Error saving tier',
          message: error?.message
        });
      }
    });
  };

  const removeEventPricingTier = () => {
    deleteEventTier(
      { tierId: selectedTicket.id },
      {
        onSuccess: () => {
          deleteTicket(selectedIndex);
          refetchEvent();
        },
        onError: (error) => {
          showError({
            title: 'Error deleting tier',
            message: error?.message
          });
        }
      }
    );
    setSelectedTicket(null);
  };

  const handleToggleTicketTier = (tierId: string, isDisabled: boolean) => {
    const ticket: TierDto = tickets.find((t) => t.id === tierId);
    const {
      type,
      name,
      description,
      price,
      isPrivate,
      requiresApproval,
      maxAttendance,
      requiredAssociationIds,
      requiredOrganizationIds,
      questions,
      imageUrl,
      startDate,
      endDate
    } = ticket;

    updateEventTier(
      {
        tierId,
        tierData: {
          type,
          name,
          description,
          price,
          isPrivate,
          requiresApproval,
          maxAttendance,
          requiredAssociationIds,
          requiredOrganizationIds,
          questions,
          imageUrl,
          startDate,
          endDate,
          isDisabled
        }
      },
      {
        onSuccess: () => {
          if (isDisabled) {
            disableTicket(tierId);
          } else {
            enableTicket(tierId);
          }
          refetchEvent();
        },
        onError: (error) => {
          showError({
            title: 'Error updating tier',
            message: error?.message
          });
        }
      }
    );
  };

  const handleReorder = (
    tiers: Array<TierDto>,
    sections: Array<SectionDto>
  ) => {
    reorderEventTierMutation.mutate({
      eventId,
      tiers: tiers.map((tier) => ({
        tierId: tier.id,
        sortIndex: tier.sortIndex,
        ...(tier.sectionId &&
          tier.sectionId !== 'default' && { sectionId: tier.sectionId })
      })),
      sections: sections
        .filter((section) => section.id !== 'default')
        .map((section) => ({
          id: section.id,
          sortIndex: section.sortIndex
        }))
    });

    setTickets(
      tickets.map((tier) => {
        const tierToMove = tiers.find((t) => t.id === tier.id);

        return {
          ...tier,
          ...(tierToMove?.sectionId && { sectionId: tierToMove.sectionId }),
          ...(tierToMove?.sortIndex >= 0 && { sortIndex: tierToMove.sortIndex })
        };
      })
    );

    setSections(
      sections.map((section) => ({
        ...section,
        sortIndex: sections.find((s) => s.id === section.id)?.sortIndex
      }))
    );
  };

  const handleToggleHideTier = (tierId: string) => {
    toggleHideTier({ eventId, tierId });
  };

  const onDeleteSection = (sectionId: string) => {
    setTickets(
      tickets.map((tier) =>
        tier.sectionId === sectionId ? { ...tier, sectionId: 'default' } : tier
      )
    );
  };

  return (
    <Modal
      visible
      onClose={onClose}
      type={ModalType.SMALL}
      title="Price tiers"
      icon={<MdConfirmationNumber size={20} />}
      isBehind={isModalVisible || selectedTicket}
      renderFooter={() => {
        if (tabIndex === PUBLIC_TAB_INDEX) {
          return (
            <Button
              type={ButtonType.Primary}
              disabled={publicTickets.length >= MAX_PUBLIC_TICKETS}
              onClick={toggleModal}
            >
              Add Public Tier
            </Button>
          );
        }

        if (tabIndex === PRIVATE_TAB_INDEX) {
          return (
            <Button
              type={ButtonType.Primary}
              disabled={privateTickets.length >= MAX_PRIVATE_TICKETS}
              onClick={toggleModal}
            >
              Add Private Tier
            </Button>
          );
        }

        if (tabIndex === COMPLIMENTARY_TAB_INDEX) {
          return (
            <Button
              type={ButtonType.Primary}
              disabled={
                complimentaryTickets?.items?.length >=
                  MAX_COMPLIMENTARY_TICKETS || !eventId
              }
              onClick={toggleModal}
            >
              Add Complimentary Ticket
            </Button>
          );
        }

        return null;
      }}
    >
      <div className="p-4">
        <FeesPaidBy />

        <div className="py-6">
          <Tabs
            selectedIndex={tabIndex}
            onSelect={(index) => setTabIndex(index)}
          >
            <TabList className="flex text-center justify-between border-b text-base border-gray-100 text-gray-500">
              <Tab
                className="w-full py-3 text-center cursor-pointer"
                selectedClassName="text-primary-500 border-b-4 border-primary-400 "
              >
                Public ({publicTickets.length}/{MAX_PUBLIC_TICKETS})
              </Tab>
              <Tab
                className="w-full py-3 text-center cursor-pointer"
                selectedClassName="text-primary-500 border-b-4 border-primary-400"
              >
                Private ({privateTickets.length}/{MAX_PRIVATE_TICKETS})
              </Tab>
              <Tab
                className="w-full py-3 text-center cursor-pointer"
                selectedClassName="text-primary-500 border-b-4 border-primary-400"
              >
                Comp ({complimentaryTickets?.items.length || 0}/
                {MAX_COMPLIMENTARY_TICKETS})
              </Tab>
            </TabList>

            <TabPanel>
              <p className="pt-6 pb-4 font-normal text-base text-gray-500">
                Public tiers are visible to everyone who visits the event
                details page.
              </p>

              <Sections
                eventId={eventId}
                sections={sections}
                tiers={publicTickets}
                onReorder={handleReorder}
                onDeleteSection={onDeleteSection}
              >
                {(tickets, sectionId) => (
                  <Tickets
                    eventId={eventId}
                    eventShareToken={eventShareToken}
                    tickets={tickets}
                    selectedIndex={selectedIndex}
                    selectedTicket={selectedTicket}
                    toggleTicketTier={handleToggleTicketTier}
                    toggleHideTier={handleToggleHideTier}
                    setSelectedIndex={setSelectedIndex}
                    setSelectedTicket={setSelectedTicket}
                    isSortable
                    sectionId={sectionId}
                    lastCreatedTicketId={lastCreatedTicketId}
                  />
                )}
              </Sections>
            </TabPanel>

            <TabPanel>
              <p className="pt-6 font-normal text-base text-gray-500">
                Private tiers are not visible on the event details page. Guests
                can join only with the specific private code, accessible by
                clicking pricing post-event creation.
              </p>

              <Tickets
                eventId={eventId}
                eventShareToken={eventShareToken}
                tickets={privateTickets}
                selectedIndex={selectedIndex}
                selectedTicket={selectedTicket}
                toggleTicketTier={handleToggleTicketTier}
                toggleHideTier={handleToggleHideTier}
                setSelectedIndex={setSelectedIndex}
                setSelectedTicket={setSelectedTicket}
                lastCreatedTicketId={lastCreatedTicketId}
              />
            </TabPanel>

            <TabPanel>
              {!eventId ? (
                <p className="pt-6 font-normal text-base text-red-500 text-center">
                  Please add an event title before creating complimentary
                  tickets.
                </p>
              ) : (
                <>
                  <p className="pt-6 font-normal text-base text-gray-500">
                    Complimentary tickets contribute to the event&apos;s max
                    capacity and appear as unclaimed on the guest list. Hosts
                    and co-hosts receive individual PDFs via email for printing.
                  </p>

                  <ComplimentaryTickets tickets={complimentaryTickets?.items} />
                </>
              )}
            </TabPanel>
          </Tabs>
        </div>
      </div>

      {/* Add Tier */}
      {isModalVisible &&
        (currentTierType === 'complimentary' ? (
          <ComplimentaryTicketForm
            eventId={eventId}
            refetchTickets={refetch}
            closeModal={() => {
              setIsModalVisible(false);
            }}
          />
        ) : (
          <TicketForm
            user={user}
            onSubmit={addEventPricingTier}
            closeModal={() => {
              setIsModalVisible(false);
            }}
            isTierPrivate={currentTierType === 'private'}
          />
        ))}

      {/* Edit Ticket */}
      {selectedTicket && (
        <TicketForm
          user={user}
          ticket={selectedTicket}
          onSubmit={updateEventPricingTier}
          closeModal={() => {
            setSelectedTicket(null);
          }}
          onRemove={removeEventPricingTier}
          isTierPrivate={currentTierType === 'private'}
        />
      )}
    </Modal>
  );
};

export default PricingTiersModal;
